import React, { useState } from 'react';
import axios from 'axios';
import { Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';
import LogoLargo from '../Img/Logo_largo.png';

const ChangePasswordModal = ({ userId, onPasswordChanged }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Ojo: Este modal es "forzado", no tiene botón de cerrar.
  // El usuario DEBE cambiar la contraseña para continuar.

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // N10: alinear con backend (auth.py:cambiar_password):
    //   >= 8 caracteres + al menos una mayuscula + al menos un digito.
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('La contraseña debe tener al menos una letra mayúscula.');
      return;
    }
    if (!/\d/.test(password)) {
      setError('La contraseña debe tener al menos un número.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);

    try {
      // Usamos el endpoint PUT que mencionaste
      const url = `https://app.legalpp.com.co/legalplus/usuario/${userId}/cambiar_password`;
      await axios.put(url, { password: password });

      setSuccess(true);
      // Esperamos 2 segundos para que el usuario vea el mensaje de éxito
      // y luego llamamos a la función del padre (App.jsx)
      setTimeout(() => {
        onPasswordChanged();
      }, 2000);

    } catch (err) {
      if (err.response) {
        setError(err.response.data.error || 'Error desconocido al actualizar.');
      } else {
        setError('No se pudo conectar al servidor.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="relative bg-slate-900/90 backdrop-blur-md border border-slate-700 p-8 rounded-3xl shadow-2xl shadow-black/60 w-full max-w-md z-10 text-white animate-scaleIn">

        <div className="flex justify-center mb-7">
          <img src={LogoLargo} alt="Logo" className="h-8 object-contain opacity-90" />
        </div>

        {!success ? (
          <>
            <div className="text-center mb-7">
              <h2 className="text-2xl font-extrabold text-white tracking-tight">Cambio de contraseña</h2>
              <p className="text-slate-400 text-sm mt-2">
                Es tu primer inicio de sesión. Por seguridad, crea una nueva contraseña.
              </p>
              <p className="text-slate-500 text-xs mt-2">
                Mínimo 8 caracteres · al menos una mayúscula · al menos un número.
              </p>
            </div>

            {error && (
              <div className="bg-red-900/30 border border-red-700/50 text-red-300 text-sm p-3 rounded-2xl mb-5 flex items-center gap-2">
                <AlertTriangle size={16} className="flex-shrink-0 text-red-400" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5" htmlFor="new-password">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder:text-slate-600 focus:border-blue-500/70 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-200 pr-11 text-sm"
                    placeholder="Ingresa tu nueva contraseña"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5" htmlFor="confirm-password">
                  Confirmar Contraseña
                </label>
                <input
                  type="password"
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder:text-slate-600 focus:border-blue-500/70 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-200 text-sm"
                  placeholder="Repite la contraseña"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm shadow-lg shadow-blue-900/30 transition-all duration-200 active:scale-95 disabled:opacity-50 mt-2"
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Actualizando...
                  </>
                ) : 'Actualizar Contraseña'}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-2xl bg-emerald-900/40 border border-emerald-700/50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={32} className="text-emerald-400" />
            </div>
            <h2 className="text-2xl font-extrabold text-white mb-2">¡Contraseña actualizada!</h2>
            <p className="text-slate-400 text-sm">Serás redirigido al dashboard en un momento...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
